/**
 * Reading a wallet's refusal apart from a wallet's failure (contracts v3, UI part §2).
 *
 * The difference is the whole message: a rejected signature means **nothing was sent** and the
 * deal is untouched — the honest answer is "Signature cancelled — nothing was sent" plus a Retry.
 * A failure means something went wrong on the way and may or may not have left a transaction. Same
 * red box for both would teach the buyer that their own "reject" broke the order.
 *
 * The two wallet families word it differently, and neither promises a stable code: viem raises
 * `UserRejectedRequestError` (EIP-1193 code 4001), TronLink answers with a string or an `Error`
 * whose message says "declined". Matched by all three, because any one of them alone has been
 * seen to change with a wallet update.
 */

const REJECTION_PATTERNS = [
  'user rejected',
  'user denied',
  'userrejected',
  'rejected the request',
  'declined',
  'cancelled by user',
  'canceled by user',
];

/** True when the user refused in their wallet — nothing left the wallet, nothing changed. */
export const isUserRejection = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  if (typeof error === 'object') {
    const candidate = error as {
      code?: unknown;
      name?: unknown;
      shortMessage?: unknown;
      message?: unknown;
    };

    // EIP-1193: 4001 is "user rejected request".
    if (candidate.code === 4001) {
      return true;
    }
    if (
      typeof candidate.name === 'string' &&
      candidate.name.toLowerCase().includes('userrejected')
    ) {
      return true;
    }
  }

  const text = typeof error === 'string' ? error : ((error as Error)?.message ?? '');

  return REJECTION_PATTERNS.some((pattern) => text.toLowerCase().includes(pattern));
};

/**
 * The buyer refused the wallet's *connect* prompt (app#204) — EIP-1193 code 4001 again, but from
 * `eth_requestAccounts`, before any signature was asked for. Left as a bare 4001 it reads as
 * "Signature cancelled", which describes a prompt the buyer never saw. Raised by the EVM hook at
 * the connect step, so the screen can tell the two refusals apart without inspecting the wallet's
 * wording a second time.
 */
export class WalletConnectionCancelledError extends Error {
  /** Still a user rejection by origin — a generic check keeps treating it as "nothing was sent". */
  readonly code = 4001;

  constructor() {
    super('Wallet connection cancelled — connect your wallet to pay.');
    this.name = 'WalletConnectionCancelledError';
  }
}

/**
 * There is no wallet to sign with: nothing announced itself in this browser and nothing is
 * connected (app#315). Until the connection window this was the "Install MetaMask" sentence — advice
 * a phone cannot follow. Now it is a state the screen answers with `Connect Wallet` and the window,
 * so the words only say what is missing, never what to install.
 */
export class NoWalletConnectedError extends Error {
  constructor() {
    super('No wallet is connected. Connect one to continue.');
    this.name = 'NoWalletConnectedError';
  }
}

/** True for the error above, by name as well as by class: a hook's copy and a screen's may differ. */
export const isNoWalletConnected = (error: unknown): boolean =>
  error instanceof NoWalletConnectedError ||
  (typeof error === 'object' &&
    error !== null &&
    (error as Error).name === 'NoWalletConnectedError');

/** A message for the buyer: their own words for a refusal, the wallet's words for anything else. */
export const walletErrorMessage = (error: unknown, fallback: string): string => {
  // Before the generic rejection check: this error carries a 4001 by origin, and the generic
  // branch would otherwise call it a cancelled signature.
  if (error instanceof WalletConnectionCancelledError) {
    return error.message;
  }

  if (isUserRejection(error)) {
    return 'Signature cancelled — nothing was sent.';
  }

  const text = typeof error === 'string' ? error : (error as Error)?.message;

  return text || fallback;
};
